import type { BsyncClient } from './client';

/**
 * Development-only email + password sign-in (enabled with VITE_DEV_EMAIL_LOGIN=1),
 * for local stacks without Google OAuth credentials. Signs in, or creates the
 * account on first use. Google sign-in stays available alongside it.
 */
export async function devEmailSignIn(client: BsyncClient, email: string, password: string): Promise<void> {
  const clean = email.trim();
  if (!clean || password.length < 6) throw new Error('Enter an email and a password of at least 6 characters.');
  const { error } = await client.auth.signInWithPassword({ email: clean, password });
  if (!error) return;
  if (!/invalid login credentials/i.test(error.message)) throw new Error(error.message);
  const { data, error: signUpError } = await client.auth.signUp({ email: clean, password });
  if (signUpError) throw new Error(signUpError.message);
  if (!data.session) throw new Error('Account created, but email confirmation is required. Disable confirmations in supabase/config.toml for local use.');
}

/** The fixed development account used by VITE_DEV_AUTO_LOGIN. */
export const DEV_USER = { email: 'dev@example.com', password: 'bsync-dev-password' } as const;

export function isLocalSupabase(url: string | undefined): boolean {
  try {
    const host = new URL(url ?? '').hostname;
    return host === '127.0.0.1' || host === 'localhost' || host === '::1' || host === '[::1]';
  } catch {
    return false;
  }
}

/**
 * Development only (VITE_DEV_AUTO_LOGIN=1): sign in as the shared dev user,
 * creating it on first use. Refuses anything but a local Supabase, so it can
 * never create accounts in a hosted project.
 */
export async function devAutoSignIn(client: BsyncClient, supabaseUrl: string | undefined): Promise<void> {
  if (!isLocalSupabase(supabaseUrl)) {
    throw new Error(`Dev auto-login only works against a local Supabase (got ${supabaseUrl}).`);
  }
  await devEmailSignIn(client, DEV_USER.email, DEV_USER.password);
}
